import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { Download, Loader2 } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

type Props = {
  /** null = 关闭 */
  target: { name: string; url: string } | null;
  onClose: () => void;
};

/** 接入点二维码:qrcode(控制台已有依赖)前端生成 PNG,可下载印到海报 / 展架上。 */
export function WebEntryQrDialog({ target, onClose }: Props) {
  const { t } = useTranslation("platform");
  const [dataUrl, setDataUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    setDataUrl("");
    if (!target?.url) return;
    QRCode.toDataURL(target.url, {
      errorCorrectionLevel: "M",
      margin: 2,
      width: 640,
      color: { dark: "#000000", light: "#FFFFFF" },
    })
      .then((d) => !cancelled && setDataUrl(d))
      .catch(() => !cancelled && setDataUrl(""));
    return () => {
      cancelled = true;
    };
  }, [target?.url]);

  const download = () => {
    if (!dataUrl || !target) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${target.name || "kefu"}-二维码.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {t("channelMgmt.webEntry.qrTitle", { name: target?.name ?? "" })}
          </DialogTitle>
          <DialogDescription>{t("channelMgmt.webEntry.qrDesc")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          {dataUrl ? (
            <img
              src={dataUrl}
              alt={t("channelMgmt.webEntry.qrAlt")}
              className="h-56 w-56 rounded-md border bg-white p-1"
              data-testid="we-qr-img"
            />
          ) : (
            <div className="flex h-56 w-56 items-center justify-center rounded-md border">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          <p className="break-all text-center text-xs text-muted-foreground">{target?.url}</p>
          <Button onClick={download} disabled={!dataUrl} data-testid="we-qr-download">
            <Download className="h-4 w-4 mr-1" />
            {t("channelMgmt.webEntry.qrDownload")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
